/**
 * Tarifs des fabricants — liste à gauche, lecture du tarif à droite (lot C1).
 *
 * Un tarif est le fichier de prix d'un fabricant (« KAESER pièces et service »). Chaque année, on y importe
 * le nouveau fichier Excel : il devient une VERSION, immuable, que l'on peut seulement archiver. L'éditeur
 * de gamme y puise, quand on tape une référence, la désignation et le prix à PROPOSER (jamais à imposer).
 *
 * MISE EN PAGE : celle de la page « Gammes » (retour de Benjamin du 2026-10-08) — liste compacte à gauche
 * avec une recherche en tête, lecture à droite, chaque colonne défile seule, l'en-tête du tarif (nom,
 * boutons, messages du dernier geste) reste collé en haut de la colonne de droite : un refus s'affiche là
 * où l'on a cliqué, jamais hors de l'écran. Les classes CSS `gammes-*` sont réutilisées telles quelles.
 *
 * DROITS (affichage seulement : l'API décide) : qui peut LIRE les gammes parcourt les tarifs (références,
 * désignations) ; les PRIX n'apparaissent que pour qui détient `pricing:read` ; créer un tarif, importer
 * et archiver exigent `pricing:manage` ET `pricing:read`.
 *
 * Tant qu'un import est ouvert, la liste de gauche est verrouillée : on ne change pas de tarif au milieu.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ApiError, api, type TarifDetail, type TarifResume, type VersionTarif } from '../api/client';
import { useDroitsGammes } from '../auth/useDroits';
import { ErreurFormulaire } from '../composants/Formulaire';
import { FormulaireTarif } from '../composants/FormulaireTarif';
import { ImportTarif } from '../composants/ImportTarif';
import { MajGammesTarif } from '../composants/MajGammesTarif';
import { RechercheTarif } from '../composants/RechercheTarif';
import { aujourdhuiLocal } from '../domaine/gammes-versions';
import { dateFrTarif, messageImportReussi, nombreFr, resumerVersionTarif } from '../domaine/tarifs';

/** Un tarif correspond-il à ce qu'on a tapé ? Sans tenir compte des majuscules ni des accents. */
function correspond(tarif: TarifResume, recherche: string): boolean {
  const norme = (s: string) =>
    s
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase();
  const cherche = norme(recherche.trim());
  if (cherche === '') return true;
  return norme(`${tarif.label} ${tarif.partyName}`).includes(cherche);
}

export function TarifsPage() {
  const [parametres, setParametres] = useSearchParams();
  const selectionne = parametres.get('tarif');
  const droits = useDroitsGammes();

  const [recherche, setRecherche] = useState(parametres.get('q') ?? '');
  const [tarifs, setTarifs] = useState<TarifResume[] | null>(null);
  const [detail, setDetail] = useState<TarifDetail | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [afficherArchives, setAfficherArchives] = useState(false);
  const [panneau, setPanneau] = useState(false);
  const [import_, setImport] = useState(false);
  /** La version de tarif d'après laquelle on met les gammes à jour (écran de revue ouvert), ou `null`. */
  const [majGammes, setMajGammes] = useState<string | null>(null);
  const [erreurAction, setErreurAction] = useState<ApiError | Error | null>(null);
  const [succes, setSucces] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [rechargement, setRechargement] = useState(0);

  const filtres = useMemo(() => (tarifs ?? []).filter((t) => correspond(t, recherche)), [tarifs, recherche]);
  const peutEcrire = droits.peutEcrirePrix;
  /** Un import ou une mise à jour des gammes est ouvert : on ne change pas de tarif et on n'ouvre rien d'autre. */
  const occupe = import_ || majGammes !== null;

  const majUrl = useCallback(
    (tarif: string | null, q: string) => {
      const p = new URLSearchParams();
      if (q.trim()) p.set('q', q);
      if (tarif) p.set('tarif', tarif);
      setParametres(p, { replace: true });
    },
    [setParametres],
  );

  const chargerListe = useCallback(async () => {
    try {
      setTarifs(await api.tarifs(afficherArchives));
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Chargement des tarifs impossible.');
    }
  }, [afficherArchives]);

  useEffect(() => {
    void chargerListe();
  }, [chargerListe]);

  // Rien n'est choisi : on montre le premier tarif de la liste plutôt qu'une colonne vide.
  useEffect(() => {
    const premier = filtres[0];
    if (tarifs !== null && !selectionne && premier) majUrl(premier.id, recherche);
  }, [tarifs, filtres, selectionne, recherche, majUrl]);

  useEffect(() => {
    setDetail(null);
    setImport(false);
    setMajGammes(null);
    if (!selectionne) return;
    let annule = false;
    api
      .tarif(selectionne)
      .then((d) => {
        if (!annule) setDetail(d);
      })
      .catch((e: unknown) => {
        if (!annule) setErreur(e instanceof ApiError ? e.message : 'Chargement du tarif impossible.');
      });
    return () => {
      annule = true;
    };
  }, [selectionne, rechargement]);

  const rafraichir = async () => {
    await chargerListe();
    setRechargement((n) => n + 1);
  };

  const agir = async (geste: () => Promise<unknown>, message: string) => {
    setEnCours(true);
    setErreurAction(null);
    setSucces(null);
    try {
      await geste();
      setSucces(message);
      await rafraichir();
    } catch (e) {
      setErreurAction(e instanceof ApiError || e instanceof Error ? e : new Error('Action impossible.'));
    } finally {
      setEnCours(false);
    }
  };

  const changerRecherche = (saisie: string) => {
    setRecherche(saisie);
    majUrl(selectionne, saisie);
  };

  const selectionner = (id: string) => {
    if (occupe) return;
    setErreurAction(null);
    setSucces(null);
    majUrl(id, recherche);
  };

  const apresImport = (version: VersionTarif) => {
    setImport(false);
    setErreurAction(null);
    setSucces(messageImportReussi(version));
    void rafraichir();
  };

  const archiverVersion = (v: VersionTarif) => {
    if (!detail) return;
    const confirme = window.confirm(
      `Archiver la version ${v.versionNumber} de « ${detail.label} » ?\n\n` +
        (v.enVigueur
          ? 'C’est la version EN VIGUEUR : la version précédente, s’il y en a une, reprendra sa place. '
          : '') +
        'Rien n’est supprimé : une version archivée n’est plus proposée dans les gammes, et reste lisible.',
    );
    if (!confirme) return;
    void agir(() => api.archiverVersionTarif(detail.id, v.id), `Version ${v.versionNumber} archivée.`);
  };

  const archiverTarif = () => {
    if (!detail) return;
    const confirme = window.confirm(
      `Archiver le tarif « ${detail.label} » ?\n\n` +
        'Il ne sera plus proposé dans les gammes. Rien n’est supprimé : ses versions restent lisibles.',
    );
    if (!confirme) return;
    void agir(() => api.archiverTarif(detail.id), 'Tarif archivé.');
  };

  const messages = (
    <>
      <ErreurFormulaire
        message={erreurAction?.message ?? null}
        details={erreurAction instanceof ApiError ? erreurAction.details : undefined}
      />
      {succes && <div className="message succes">{succes}</div>}
    </>
  );

  const aujourdhui = aujourdhuiLocal();

  return (
    <div className="gammes-page">
      <div className="entete-page">
        <div>
          <h1>Tarifs des fabricants</h1>
          <p>
            Le fichier de prix de chaque fabricant. Quand vous tapez une référence dans une gamme, sa désignation et
            son prix sont proposés d’après le tarif.
          </p>
        </div>
        {peutEcrire && (
          <button disabled={occupe} onClick={() => setPanneau(true)}>
            Nouveau tarif
          </button>
        )}
      </div>

      {!peutEcrire && (
        <div className="message info">
          Vous consultez les tarifs en lecture seule : en créer ou y importer un fichier demande le droit de gérer
          les prix.
          {!droits.peutVoirPrix && ' Les prix eux-mêmes ne vous sont pas montrés.'}
        </div>
      )}

      <div className="gammes-mise-en-page">
        <div className="carte gammes-liste">
          <div className="gammes-liste-entete">
            <input
              type="text"
              autoFocus
              aria-label="Rechercher un tarif"
              placeholder="Rechercher : fabricant, libellé…"
              value={recherche}
              disabled={occupe}
              onChange={(e) => changerRecherche(e.target.value)}
            />
            {occupe && (
              <p className="aide">
                {import_ ? 'Un import' : 'Une mise à jour des gammes'} est en cours : terminez-{import_ ? 'le' : 'la'} ou
                annulez-{import_ ? 'le' : 'la'} pour changer de tarif.
              </p>
            )}
            <div className="gammes-liste-outils">
              <span className="aide">
                {tarifs === null
                  ? 'Chargement…'
                  : filtres.length === tarifs.length
                    ? `${tarifs.length} tarif${tarifs.length > 1 ? 's' : ''}`
                    : `${filtres.length} sur ${tarifs.length}`}
              </span>
              <label className="case">
                <input
                  type="checkbox"
                  checked={afficherArchives}
                  disabled={occupe}
                  onChange={(e) => setAfficherArchives(e.target.checked)}
                />
                Afficher les archivés
              </label>
            </div>
          </div>

          <div className="gammes-liste-corps">
            {erreur && <div className="message erreur">{erreur}</div>}
            {tarifs === null ? (
              !erreur && <div className="vide">Chargement…</div>
            ) : tarifs.length === 0 ? (
              <div className="vide">
                Aucun tarif pour l’instant.
                {peutEcrire && ' Créez-en un pour y importer le fichier d’un fabricant.'}
              </div>
            ) : filtres.length === 0 ? (
              <div className="vide">Aucun tarif ne correspond à « {recherche.trim()} ».</div>
            ) : (
              <ul className="parc-lignes">
                {filtres.map((t) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      className={`parc-ligne${t.id === selectionne ? ' actif' : ''}`}
                      aria-pressed={t.id === selectionne}
                      disabled={occupe}
                      onClick={() => selectionner(t.id)}
                    >
                      <div className="parc-ligne-titre">
                        <strong>{t.label}</strong>
                        {t.archivedAt && <span className="etat revoque">Archivé</span>}
                      </div>
                      <div className="aide">{t.partyName}</div>
                      <div className="gammes-ligne-etat">
                        {t.versionEnVigueur ? (
                          <span className="aide">
                            v{t.versionEnVigueur.versionNumber} depuis le {dateFrTarif(t.versionEnVigueur.validFrom)} ·{' '}
                            {nombreFr(t.versionEnVigueur.lineCount)} lignes
                          </span>
                        ) : (
                          <span className="etat attente">Aucune version en vigueur</span>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="gammes-fiche">
          {!detail && (erreurAction || succes) && <div className="gammes-fiche-entete">{messages}</div>}

          {!selectionne && (
            <div className="carte">
              <div className="vide">
                Choisissez un tarif dans la liste pour le lire ici. Une version importée ne change plus jamais : un
                nouveau fichier crée une nouvelle version.
              </div>
            </div>
          )}

          {selectionne && !detail && !erreur && <div className="vide">Chargement du tarif…</div>}

          {detail && (
            <>
              <div className="gammes-fiche-entete">
                <div className="gammes-fiche-titre">
                  <h2>
                    {detail.label} <span className="aide">({detail.partyName})</span>
                    {detail.archivedAt && (
                      <>
                        {' '}
                        <span className="etat revoque">Archivé</span>
                      </>
                    )}
                  </h2>
                  {peutEcrire && !detail.archivedAt && (
                    <div className="saisie-ligne">
                      <button disabled={enCours || occupe} onClick={() => setImport(true)}>
                        Importer une nouvelle version
                      </button>
                      <button className="secondaire danger-discret" disabled={enCours || occupe} onClick={archiverTarif}>
                        Archiver
                      </button>
                    </div>
                  )}
                </div>
                {messages}
              </div>

              {import_ && (
                <div className="carte">
                  <div className="carte-corps">
                    <ImportTarif tarif={detail} onPublie={apresImport} onFermer={() => setImport(false)} />
                  </div>
                </div>
              )}

              {majGammes !== null && (
                <div className="carte">
                  <div className="carte-corps">
                    {(() => {
                      const cible = detail.versions.find((v) => v.id === majGammes);
                      return cible ? (
                        <MajGammesTarif
                          tarif={detail}
                          version={cible}
                          droits={droits}
                          onFermer={() => setMajGammes(null)}
                          onBrouillonsCrees={(message) => {
                            setErreurAction(null);
                            setSucces(message);
                          }}
                        />
                      ) : (
                        <div className="message erreur">Cette version de tarif n’existe plus : fermez et recommencez.</div>
                      );
                    })()}
                  </div>
                </div>
              )}

              <div className="carte">
                <h2>Versions</h2>
                <div className="carte-corps">
                  {detail.versions.length === 0 ? (
                    <div className="vide">
                      Ce tarif n’a encore aucune version.
                      {peutEcrire && !detail.archivedAt && ' Importez le fichier Excel du fabricant pour commencer.'}
                    </div>
                  ) : (
                    <ul className="liste-regles-rattachees">
                      {detail.versions.map((v) => (
                        <li key={v.id}>
                          <span>
                            <strong>{resumerVersionTarif(v)}</strong>{' '}
                            {v.enVigueur ? (
                              <span className="etat actif">En vigueur</span>
                            ) : v.archivedAt ? (
                              <span className="etat revoque">Archivée</span>
                            ) : v.validFrom > aujourdhui ? (
                              <span className="etat attente">À venir</span>
                            ) : (
                              <span className="etat attente">Remplacée</span>
                            )}
                            <br />
                            <span className="aide">Fichier : {v.fileName}</span>
                          </span>
                          <div className="saisie-ligne">
                            {/* Droit de GÉRER LES GAMMES, pas celui d'écrire les prix : mettre des désignations à jour ne
                                demande pas de toucher aux prix (la revue masque ceux-ci à qui ne les voit pas). */}
                            {droits.peutGerer && !v.archivedAt && !detail.archivedAt && (v.enVigueur || v.validFrom > aujourdhui) && (
                              <button
                                className="secondaire discret"
                                disabled={enCours || occupe}
                                onClick={() => {
                                  setErreurAction(null);
                                  setSucces(null);
                                  setMajGammes(v.id);
                                }}
                              >
                                Mettre à jour les gammes d’après cette version
                              </button>
                            )}
                            {peutEcrire && !v.archivedAt && !detail.archivedAt && (
                              <button
                                className="secondaire discret danger-discret"
                                disabled={enCours || occupe}
                                onClick={() => archiverVersion(v)}
                              >
                                Archiver cette version
                              </button>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div className="carte">
                <h2>Chercher dans le tarif</h2>
                <div className="carte-corps">
                  <RechercheTarif key={`${detail.id}-${rechargement}`} tarifId={detail.id} />
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {panneau && (
        <FormulaireTarif
          onFermer={() => setPanneau(false)}
          onCree={(t) => {
            setPanneau(false);
            setErreurAction(null);
            setSucces(`Tarif « ${t.label} » créé. Importez maintenant le fichier Excel du fabricant.`);
            majUrl(t.id, '');
            setRecherche('');
            void chargerListe();
          }}
        />
      )}
    </div>
  );
}
